<script lang="ts">
  import { mdiClockOutline, mdiPencil } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import type { AvailabilityPolicyConfig } from "@kontextwerk/control-web-client"
  import { buildServiceHoursPreview } from "../../../../../../../../../_shared/_logic/service-hours-presentation"

  const {
    serviceHours = null,
    disabled = false,
    onEdit,
  } = $props<{
    serviceHours?: AvailabilityPolicyConfig | null
    disabled?: boolean
    onEdit: () => void
  }>()

  const preview = $derived(buildServiceHoursPreview(serviceHours))
</script>

<section
  class="service-hours-preview"
  data-testid="kehrblatt.organizationSettings.serviceHoursPreview"
>
  <SectionTitle
    title="Servicezeiten"
    small={true}
    brightBackground={true}
    helpModalTextId="organizationServiceHours"
  />

  <ActionBox
    label="Servicezeiten"
    tag={preview.statusTag}
    tagColor={preview.statusColor}
    tagVariant="soft"
    darkMode={true}
  >
    <div class="preview-content">
      <section class="preview-info-block opening-hours-block" aria-label="Öffnungszeiten">
        <h3 class="preview-title">
          <span class="preview-title-icon" aria-hidden="true">
            <Icon path={mdiClockOutline} size="1rem" color="currentColor" />
          </span>
          <span>Öffnungszeiten</span>
        </h3>
        <div class="preview-value-group">
          {#each preview.regularLines as line, index}
            <span class:primary-line={index === 0}>{line}</span>
          {/each}
          {#if preview.hasMoreRegularLines}
            <span class="more-line">Weitere Zeiten vorhanden</span>
          {/if}
        </div>
      </section>
      <dl class="preview-meta">
        <div class="preview-info-block">
          <dt>Ausgeschlossene Tage</dt>
          <dd>{preview.closedDatesLine}</dd>
        </div>
        <div class="preview-info-block">
          <dt>Feiertage</dt>
          <dd>{preview.holidaysLine}</dd>
        </div>
      </dl>
    </div>

    {#snippet buttonSnippet()}
      <CTA
        type="primaryButton"
        content="Servicezeiten bearbeiten"
        icon={mdiPencil}
        onClick={onEdit}
        {disabled}
        small={true}
      />
    {/snippet}
  </ActionBox>
</section>

<style lang="less">
  .service-hours-preview {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .preview-content {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    gap: 1rem clamp(1rem, 3vw, 2rem);
    align-items: start;
    color: var(--text-invers-100);
  }

  .preview-info-block,
  .preview-value-group {
    display: flex;
    flex-direction: column;
  }

  .preview-info-block {
    min-width: 0;
    gap: 0.45rem;
    padding-left: 0.8rem;
    border-left: 2px solid var(--primary-100);
  }

  .preview-title {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    margin: 0;
    color: var(--primary-100);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0;
    line-height: 1.15;
    text-transform: uppercase;
  }

  .preview-title-icon {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
  }

  .preview-value-group {
    display: flex;
    gap: 0.25rem;
    color: var(--text-invers-100);
    font-size: 0.9rem;
    line-height: 1.35;

    span {
      color: var(--text-invers-100);
    }

    .primary-line {
      color: var(--text-invers-100);
      font-size: 0.94rem;
      font-weight: 600;
    }

    .more-line {
      color: var(--text-invers-100);
    }
  }

  .preview-meta {
    display: contents;
    margin: 0;

    dt {
      margin-bottom: 0.2rem;
      color: var(--primary-100);
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0;
      line-height: 1.15;
      text-transform: uppercase;
    }

    dd {
      margin: 0;
      color: var(--text-invers-100);
      font-size: 0.9rem;
      line-height: 1.35;
    }
  }

  @media (max-width: 920px) {
    .preview-content {
      grid-template-columns: 1fr;
    }
  }
</style>
